import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const TEXT = {
  es: {
    openButton: 'Importar CSV/Excel',
    title: 'Importar productos',
    intro: 'Sube un archivo .csv o .xlsx con tus productos. La columna sku es obligatoria; las dem\u00e1s son opcionales y una celda vac\u00eda no cambia nada.',
    excelNote: 'Consejo: en Excel, pon la columna sku en formato Texto para no perder ceros iniciales (00123).',
    template: 'Descargar plantilla',
    templateFile: 'plantilla_productos.csv',
    templateLines: ['sku;nombre;costo;precio_venta;stock;stock_minimo', 'CAM-001;Camiseta b\u00e1sica negra;4,50;14,90;25;5'],
    chooseFile: 'Elegir archivo',
    analyzing: 'Analizando archivo...',
    applying: 'Guardando cambios...',
    toUpdate: 'Se actualizar\u00e1n',
    toCreate: 'Se crear\u00e1n',
    unchanged: 'Sin cambios',
    errors: 'Con errores',
    unknownColumns: 'Columnas ignoradas (no reconocidas):',
    stockWarning: 'Esta importaci\u00f3n cambiar\u00e1 el stock de algunos productos. Si entra un pedido mientras tanto, el valor del archivo lo sustituir\u00e1.',
    errorsTitle: 'Filas con errores (se ignorar\u00e1n)',
    warningsTitle: 'Avisos (se importar\u00e1n igualmente: rev\u00edsalos)',
    formatShopify: 'Formato detectado: exportaci\u00f3n de productos de Shopify (costos incluidos)',
    updatesTitle: 'Cambios en productos existentes',
    createsTitle: 'Productos nuevos',
    row: 'Fila',
    sku: 'SKU',
    name: 'Nombre',
    message: 'Problema',
    changes: 'Cambios',
    truncated: 'Se muestran solo las primeras 200 filas de cada lista.',
    confirm: 'Confirmar importaci\u00f3n',
    cancel: 'Cerrar',
    nothing: 'No hay cambios que aplicar.',
    success: 'Importaci\u00f3n completada: {{created}} creados, {{updated}} actualizados.',
    genericError: 'No se pudo procesar el archivo.',
    fields: { name: 'nombre', cogs: 'costo', sale_price: 'precio de venta', current_stock: 'stock', min_stock_alert: 'stock m\u00ednimo' },
  },
  en: {
    openButton: 'Import CSV/Excel',
    title: 'Import products',
    intro: 'Upload a .csv or .xlsx file with your products. The sku column is required; the others are optional and an empty cell changes nothing.',
    excelNote: 'Tip: in Excel, set the sku column to Text format so leading zeros (00123) are not lost.',
    template: 'Download template',
    templateFile: 'products_template.csv',
    templateLines: ['sku,name,cost,price,stock,stock_min', 'TSH-001,Basic black t-shirt,4.50,14.90,25,5'],
    chooseFile: 'Choose file',
    analyzing: 'Analyzing file...',
    applying: 'Saving changes...',
    toUpdate: 'Will be updated',
    toCreate: 'Will be created',
    unchanged: 'Unchanged',
    errors: 'With errors',
    unknownColumns: 'Ignored columns (not recognized):',
    stockWarning: 'This import will change the stock of some products. If an order comes in meanwhile, the file value will replace it.',
    errorsTitle: 'Rows with errors (they will be skipped)',
    warningsTitle: 'Warnings (they will still be imported: please review them)',
    formatShopify: 'Detected format: Shopify products export (costs included)',
    updatesTitle: 'Changes to existing products',
    createsTitle: 'New products',
    row: 'Row',
    sku: 'SKU',
    name: 'Name',
    message: 'Problem',
    changes: 'Changes',
    truncated: 'Only the first 200 rows of each list are shown.',
    confirm: 'Confirm import',
    cancel: 'Close',
    nothing: 'There are no changes to apply.',
    success: 'Import completed: {{created}} created, {{updated}} updated.',
    genericError: 'The file could not be processed.',
    fields: { name: 'name', cogs: 'cost', sale_price: 'sale price', current_stock: 'stock', min_stock_alert: 'minimum stock' },
  },
  fr: {
    openButton: 'Importer CSV/Excel',
    title: 'Importer des produits',
    intro: 'Importez un fichier .csv ou .xlsx avec vos produits. La colonne sku est obligatoire ; les autres sont facultatives et une cellule vide ne modifie rien.',
    excelNote: 'Astuce : dans Excel, mettez la colonne sku au format Texte pour ne pas perdre les z\u00e9ros initiaux (00123).',
    template: 'T\u00e9l\u00e9charger le mod\u00e8le',
    templateFile: 'modele_produits.csv',
    templateLines: ['sku;nom;cout;prix;stock;stock_min', 'TSH-001;T-shirt basique noir;4,50;14,90;25;5'],
    chooseFile: 'Choisir un fichier',
    analyzing: 'Analyse du fichier...',
    applying: 'Enregistrement des modifications...',
    toUpdate: 'Seront mis \u00e0 jour',
    toCreate: 'Seront cr\u00e9\u00e9s',
    unchanged: 'Sans changement',
    errors: 'Avec erreurs',
    unknownColumns: 'Colonnes ignor\u00e9es (non reconnues) :',
    stockWarning: 'Cette importation modifiera le stock de certains produits. Si une commande arrive entre-temps, la valeur du fichier la remplacera.',
    errorsTitle: 'Lignes avec erreurs (elles seront ignor\u00e9es)',
    warningsTitle: 'Avertissements (import\u00e9es quand m\u00eame : \u00e0 v\u00e9rifier)',
    formatShopify: 'Format d\u00e9tect\u00e9 : export des produits Shopify (co\u00fbts inclus)',
    updatesTitle: 'Modifications des produits existants',
    createsTitle: 'Nouveaux produits',
    row: 'Ligne',
    sku: 'SKU',
    name: 'Nom',
    message: 'Probl\u00e8me',
    changes: 'Modifications',
    truncated: 'Seules les 200 premi\u00e8res lignes de chaque liste sont affich\u00e9es.',
    confirm: 'Confirmer l\u2019importation',
    cancel: 'Fermer',
    nothing: 'Aucune modification \u00e0 appliquer.',
    success: 'Importation termin\u00e9e : {{created}} cr\u00e9\u00e9s, {{updated}} mis \u00e0 jour.',
    genericError: 'Impossible de traiter le fichier.',
    fields: { name: 'nom', cogs: 'co\u00fbt', sale_price: 'prix de vente', current_stock: 'stock', min_stock_alert: 'stock minimum' },
  },
};

export function getImportText(lang) {
  const code = (lang || 'es').slice(0, 2);
  return TEXT[code] || TEXT.es;
}

const fillTemplate = (text, values) =>
  text.replace(/\{\{(\w+)\}\}/g, (_, key) => (values[key] !== undefined ? String(values[key]) : ''));

const formatValue = (value) =>
  value === null || value === undefined || value === '' ? '\u2014' : String(value);

function ProductImport({ token, onAuthError, onClose, onImported }) {
  const { i18n } = useTranslation();
  const tx = getImportText(i18n.language);
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sendFile = async (selectedFile, dryRun) => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    const response = await axios.post(
      `${API_URL}/api/v1/products/import?dry_run=${dryRun ? 'true' : 'false'}`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const handleError = (err) => {
    if (err.response && err.response.status === 401) {
      onAuthError();
      return;
    }
    const detail = err.response && err.response.data && err.response.data.detail;
    setError(typeof detail === 'string' ? detail : tx.genericError);
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!selected) return;
    setFile(selected);
    setPreview(null);
    setError('');
    setSuccess('');
    setBusy('analyzing');
    try {
      const result = await sendFile(selected, true);
      setPreview(result);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy('');
    }
  };

  const confirmImport = async () => {
    if (!file) return;
    setBusy('applying');
    setError('');
    try {
      const result = await sendFile(file, false);
      setSuccess(fillTemplate(tx.success, { created: result.summary.to_create, updated: result.summary.to_update }));
      setPreview(null);
      setFile(null);
      if (onImported) onImported();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy('');
    }
  };

  const downloadTemplate = () => {
    const content = '\uFEFF' + tx.templateLines.join('\r\n') + '\r\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = tx.templateFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const summary = preview ? preview.summary : null;
  const nothingToApply = !summary || summary.to_create + summary.to_update === 0;
  const changesStock = !!preview && preview.to_update.some((item) => item.changes.current_stock !== undefined);
  const truncated = !!summary && (summary.to_update > 200 || summary.to_create > 200 || summary.errors > 200);

  const thClass = 'text-muted text-xs text-left pb-2 pr-4 uppercase';
  const tdClass = 'py-2 pr-4 text-sm';

  return (
    <div className="bg-surface border border-edge rounded-2xl p-5 mb-6">
      <h3 className="text-white text-lg font-bold">{tx.title}</h3>
      <p className="text-muted text-sm mt-1">{tx.intro}</p>
      <p className="text-muted text-xs mt-2">{tx.excelNote}</p>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          type="button"
          onClick={downloadTemplate}
          className="bg-surfaceAlt border border-edge text-white text-sm px-4 py-2 rounded-lg cursor-pointer"
        >
          {tx.template}
        </button>
        <button
          type="button"
          onClick={() => fileInput.current && fileInput.current.click()}
          disabled={!!busy}
          className="bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer"
        >
          {tx.chooseFile}
        </button>
        <input ref={fileInput} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileChange} />
      </div>

      {file && <p className="text-muted text-xs mt-2">{file.name}</p>}
      {busy && <p className="text-muted text-sm mt-4">{busy === 'applying' ? tx.applying : tx.analyzing}</p>}
      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mt-4">{error}</div>
      )}
      {success && (
        <div className="bg-[#0f2a14] border border-[#1d7f3a] text-accent text-sm rounded-lg p-3 mt-4">{success}</div>
      )}

      {preview && !busy && (
        <div className="mt-5">
          {preview.format === 'shopify' && <p className="text-muted text-xs mb-3">{tx.formatShopify}</p>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              [tx.toUpdate, summary.to_update, 'text-white'],
              [tx.toCreate, summary.to_create, 'text-accent'],
              [tx.unchanged, summary.unchanged, 'text-muted'],
              [tx.errors, summary.errors, summary.errors > 0 ? 'text-red-400' : 'text-muted'],
            ].map(([label, value, color]) => (
              <div key={label} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                <p className="text-muted text-xs">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {preview.unknown_columns && preview.unknown_columns.length > 0 && (
            <p className="text-muted text-xs mt-3">
              {tx.unknownColumns} {preview.unknown_columns.join(', ')}
            </p>
          )}

          {changesStock && (
            <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-3 mt-4">
              <p className="text-amber-400 text-sm">{tx.stockWarning}</p>
            </div>
          )}

          {preview.errors.length > 0 && (
            <div className="mt-5">
              <h4 className="text-red-400 text-sm font-bold mb-2">{tx.errorsTitle}</h4>
              <div className="max-h-64 overflow-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className={thClass}>{tx.row}</th>
                      <th className={thClass}>{tx.sku}</th>
                      <th className={thClass}>{tx.message}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((item, idx) => (
                      <tr key={`e-${idx}`} className="border-b border-surfaceAlt">
                        <td className={`${tdClass} text-muted`}>{item.row}</td>
                        <td className={`${tdClass} text-[#888] font-mono`}>{formatValue(item.sku)}</td>
                        <td className={`${tdClass} text-red-400`}>{item.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.warnings && preview.warnings.length > 0 && (
            <div className="mt-5">
              <h4 className="text-amber-400 text-sm font-bold mb-2">{tx.warningsTitle}</h4>
              <div className="max-h-64 overflow-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className={thClass}>{tx.row}</th>
                      <th className={thClass}>{tx.sku}</th>
                      <th className={thClass}>{tx.message}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.warnings.map((item, idx) => (
                      <tr key={`w-${idx}`} className="border-b border-surfaceAlt">
                        <td className={`${tdClass} text-muted`}>{item.row}</td>
                        <td className={`${tdClass} text-[#888] font-mono`}>{item.sku}</td>
                        <td className={`${tdClass} text-amber-400`}>{item.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.to_update.length > 0 && (
            <div className="mt-5">
              <h4 className="text-white text-sm font-bold mb-2">{tx.updatesTitle}</h4>
              <div className="max-h-64 overflow-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className={thClass}>{tx.sku}</th>
                      <th className={thClass}>{tx.name}</th>
                      <th className={thClass}>{tx.changes}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.to_update.map((item) => (
                      <tr key={`u-${item.sku}`} className="border-b border-surfaceAlt">
                        <td className={`${tdClass} text-[#888] font-mono`}>{item.sku}</td>
                        <td className={`${tdClass} text-white`}>{item.name}</td>
                        <td className={`${tdClass} text-muted`}>
                          {Object.keys(item.changes)
                            .map((field) => `${tx.fields[field] || field}: ${formatValue(item.before[field])} \u2192 ${formatValue(item.changes[field])}`)
                            .join(' \u00b7 ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.to_create.length > 0 && (
            <div className="mt-5">
              <h4 className="text-accent text-sm font-bold mb-2">{tx.createsTitle}</h4>
              <div className="max-h-64 overflow-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-edge">
                      <th className={thClass}>{tx.sku}</th>
                      <th className={thClass}>{tx.name}</th>
                      <th className={thClass}>{tx.changes}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.to_create.map((item) => (
                      <tr key={`c-${item.sku}`} className="border-b border-surfaceAlt">
                        <td className={`${tdClass} text-[#888] font-mono`}>{item.sku}</td>
                        <td className={`${tdClass} text-white`}>{item.name}</td>
                        <td className={`${tdClass} text-muted`}>
                          {Object.keys(item)
                            .filter((field) => field !== 'sku' && field !== 'name')
                            .map((field) => `${tx.fields[field] || field}: ${formatValue(item[field])}`)
                            .join(' \u00b7 ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {truncated && <p className="text-muted text-xs mt-3">{tx.truncated}</p>}

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <button
              type="button"
              onClick={confirmImport}
              disabled={nothingToApply || !!busy}
              className={`font-bold text-sm px-5 py-2.5 rounded-lg ${nothingToApply ? 'bg-surfaceAlt text-muted cursor-not-allowed' : 'bg-accent text-panel cursor-pointer'}`}
            >
              {tx.confirm}
            </button>
            {nothingToApply && <span className="text-muted text-sm">{tx.nothing}</span>}
          </div>
        </div>
      )}

      <div className="mt-5">
        <button
          type="button"
          onClick={onClose}
          className="bg-transparent border border-edge text-muted text-xs px-3 py-1.5 rounded cursor-pointer"
        >
          {tx.cancel}
        </button>
      </div>
    </div>
  );
}

export default ProductImport;